import { getRandomUUID } from 'shared/lib/core/lib/utils';
import { useAppDispatch, useAppSelector } from 'shared/lib/store';
import {
	createOrder,
	editBooking,
	editOrder,
	resetBookingData,
	resetOrderDetail,
	selectBookingApplicationMod,
	selectBookingThreadMod,
	selectOrderBookingData,
	selectOrderDetail,
	setBookingThreadMod,
	toBook,
	toCancelBooking,
	toWaitingList,
	updateOrderDetail,
	updateOrderGroupItem,
	updateWaitingOrderItem,
	useOrderState,
} from 'entities/orders';
import { useCallback, useState } from 'react';
import { useClientForm } from 'features/booking/BookingClientForm';
import { useLocation } from 'react-router-dom';
import { isRejectedWithValue, PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { Deposit, Order, OrderBookingPerson, PostOrderResponce } from 'entities/orders/model/types';
import { usePaginationFilter } from 'shared/lib/pagination';
import { CustomError } from 'shared/api';
import { useBookingReadyCheck } from './useBookingReadyCheck';
// import { getOrdersNext } from 'entities/orders/model/ordersThunk';
import { isSameDay } from 'date-fns';
import { updateBookingDepositAllow } from 'entities/orders/model/ordersSlice';
// import { selectPaymentProviders } from 'entities/orders/model/selectors';

interface UseBookingActionsParams {
	toProhibitClose: (isClose?: boolean) => void;
	onErrorBookingActions: (errors: string) => void;
	disableBooking: () => void;
	isNewBooking: boolean;
	successClose: (force?: boolean) => void;
	idempotencyKeyRef: React.MutableRefObject<`${string}-${string}-${string}-${string}-${string}` | string>;
	prohibitBookingAgain?: boolean;
	getWaitingOrdersList: () => void;
	enableBooking: () => void;
}

interface TBeforeSaveOrderPayload {
  id: number | undefined;
  body: {
    notes: string;
    prepayment_amount: string;
    person: OrderBookingPerson;
    tags: string[];
    deposit?: Deposit
  };
}

export const useBookingActions = ({
	toProhibitClose,
	isNewBooking,
	idempotencyKeyRef,
	prohibitBookingAgain = false,
	onErrorBookingActions,
	disableBooking,
	successClose,
  enableBooking,
}: UseBookingActionsParams) => {
	const bookingData = useAppSelector(selectOrderBookingData);
	const order = useAppSelector(selectOrderDetail);
	const { isWaitingList } = useOrderState(order);
	const bookingThreadMod = useAppSelector(selectBookingThreadMod);
	const appMod = useAppSelector(selectBookingApplicationMod);
  // const paymentProviders = useAppSelector(selectPaymentProviders);
	const dispatch = useAppDispatch();

	const [toastAwaitId, setToastAwaitId] = useState<number | string>('');

	const { formMethods } = useClientForm();
	const { pathname } = useLocation();

	const paginationFilter = usePaginationFilter();

	const { readySaveCheck, readySaveOrBookingCheck } = useBookingReadyCheck();

	const scrollToError = () => {
		const errors = Object.keys(formMethods.formState.errors);
		if (errors.length) {
			const input = document.querySelector(`input[name=${errors[0]}]`);

			input?.scrollIntoView({
				behavior: 'smooth',
				block: 'center',
				inline: 'start',
			});
		}
	};

	const bookOrEdit = useCallback(async (): Promise<
		PayloadAction<PostOrderResponce | CustomError | undefined>
	> => {
		let result;
		const prepareData = {
			...bookingData,
			idempotencyKey: idempotencyKeyRef.current,
		};

    if (!prepareData.deposit?.amount||typeof prepareData.deposit?.amount === 'string') {
      delete prepareData.deposit;
    }

    await dispatch(updateBookingDepositAllow(false));

		if (isNewBooking) {
			if (bookingThreadMod) {
				prepareData.auto_start_visit = true;
			}

			result = await dispatch(toBook(prepareData));
		} else {
			result = await dispatch(editBooking(prepareData));
		}

		if (result.type === 'toBook/rejected' || result.type === 'editBooking/rejected') {
			idempotencyKeyRef.current = crypto?.randomUUID ? crypto.randomUUID() : getRandomUUID();
		}

    setTimeout(() => {
      dispatch(updateBookingDepositAllow(true));
    }, 1000);

		return result;
	}, [
		bookingData,
		dispatch,
		isNewBooking,
		idempotencyKeyRef,
		bookingThreadMod,
	]);

	const book = async (): Promise<PayloadAction<
		PostOrderResponce | CustomError | undefined
	> | void> => {
		const isValid = await formMethods.trigger(['phone', 'name']);
		if (!isValid) {
			scrollToError();
		}

		if (isValid && !prohibitBookingAgain) {
			const result = await bookOrEdit();
			if (isRejectedWithValue(result)) {
				return result;
			}

			const response = result?.payload as PostOrderResponce;
			const responseStatus = response?.status;

			if (responseStatus === 99) {

				onErrorBookingActions(response?.errors);
			} else {
				if (!appMod) {
					const toastId = toast.info(
						'Проверяем доступность ресурсов...',
						{ autoClose: false },
					);
					setToastAwaitId(toastId);
					disableBooking();
					toProhibitClose();
				}
			}
		}
	};

  const getDepositeValid = () => {
    // if (typeof bookingData.deposit?.amount === 'number'&&bookingData.deposit?.amount>0&&bookingData.deposit?.provider_id) {
    //   const provider = paymentProviders.find(el=>el.id===bookingData.deposit?.provider_id);

    //   if (provider?.backend.is_managed&&!bookingData.person.phone?.trim()) {
    //     toast.error(`Для типа оплаты ${provider.backend.verbose_name} обязателен номер телефона`);
    //     return false;
    //   }
    // }

    return true;
  }

	const saveOrderOrBook = async () => {
    // disableBooking();
    if (!getDepositeValid()) {
      return enableBooking();
    }
		if (bookingData.id) {
			if (readySaveCheck()) {
				const isValid = await formMethods.trigger(['phone', 'name']);
				if (!isValid) {
					scrollToError();
				}

				if (isValid) {
          enableBooking();
					return await fastSaveOrder();
				}
			} else {
        enableBooking();
				return await saveOrderOrBooking();
			}
		} else {
			if (appMod) {
				const isValid = await formMethods.trigger(['phone', 'name']);
				if (!isValid) {
					scrollToError();
				}

				if (isValid) {
					const result = await addToWaitingList();
					if (!isRejectedWithValue(result)) {
						toast.success(
							`Заявка на имя ${bookingData?.person?.name} успешно создана!`,
						);
						successClose();
						paginationFilter?.fetchDataWithDispatch();
					}
				}
        enableBooking();
			} else {
				return await book();
			}
		}
	};

	const fastSaveOrder = async () => {
		const saveResult = await saveOrder();
		if (!saveResult) {
			return;
		}
		if (!isRejectedWithValue(saveResult)) {
			successClose();
			toast.success(
				`Изменения в ${
					isWaitingList ? 'заявке' : 'бронировани'
				} на имя ${bookingData?.person?.name} сохранены!`,
			);
			// dispatch(getOrdersNext({}))
			paginationFilter?.fetchDataWithDispatch();
			if (isWaitingList) {
				if ( order && !isSameDay(new Date(order?.dt_start), new Date(bookingData?.dt_start))) {
					dispatch(resetOrderDetail());
					dispatch(resetBookingData());
					dispatch(setBookingThreadMod({ isWork: false }));
					
					return;
				} 
			}
		}

		return saveResult;
	};

	const addToWaitingList = async () => {
		const isValid = await formMethods.trigger(['phone', 'name']);
		if (!isValid) {
			scrollToError();
			return;
		}
		toProhibitClose();
		disableBooking();
		let result;
		if (order?.id) {
			result = await dispatch(toWaitingList({ id: order?.id }));
		} else {
			const response = await dispatch(createOrder(bookingData));
			if (isRejectedWithValue(response)) {
				return response;
			}

			const orderId = response.payload.id;
			if (orderId) {
				result = await dispatch(toWaitingList({ id: orderId }));
			}
		}

		return result;
	};

	const removeFromWaitingList = async () => {
		toProhibitClose();
		disableBooking();
		let result;
		if (order?.id) {
			result = await dispatch(toCancelBooking({ id: order?.id }));
		}

		return result;
	};

	const canFetchAfterSave = () => {
		return (
			paginationFilter?.filters['status'] ||
			paginationFilter?.filters['tags'] ||
			paginationFilter?.filters['participants_count__gte'] ||
			paginationFilter?.filters['participants_count__lte']
		);
	};

	const saveOrder = async () => {
		const isValid = await formMethods.trigger(['phone', 'name']);
		if (!isValid) {
			scrollToError();
		}

		const conditionBookingDataId = bookingData.id && isValid;
		const conditionBookingDataAndAppMod =
			bookingData.id && isValid && appMod;

		if (conditionBookingDataId || conditionBookingDataAndAppMod) {
      const responce = await beforeSaveOrder();

			if (!isRejectedWithValue(responce)) {
        afterSaveOrder(responce);
			}

			return responce;
		}
	};

  const beforeSaveOrder = async () => {
    await toProhibitClose();
    const payload: TBeforeSaveOrderPayload = {
      id: bookingData.id,
      body: {
        notes: bookingData.notes,
        prepayment_amount: bookingData.prepayment_amount,
        person: bookingData.person,
        tags: bookingData.tags,
      }
    }
    // if (bookingData.deposit?.amount&&typeof bookingData.deposit?.amount==='number'&&bookingData.deposit?.amount > 0) {
    //   payload.body.deposit = bookingData.deposit;
    // }
    const responce = await dispatch(editOrder(payload));

    return responce;
  }

  const afterSaveOrder = (responce: Order | any) => {
    const responcePayload = {...bookingData, ...responce.payload} as any as Order;
    dispatch(updateOrderDetail(responcePayload));
    if (pathname === '/waiting-list' || pathname === '/bookings') {
      if (canFetchAfterSave()) {
        paginationFilter?.fetchDataWithDispatch();
      } else {
        if (pathname === '/bookings') {
          dispatch(
            updateOrderGroupItem({
              id: responcePayload.id,
              updates: bookingData,
            }),
          );
        } else {
          dispatch(
            updateWaitingOrderItem({
              id: responcePayload.id,
              updates: bookingData,
            }),
          );
        }
      }
    }
  }

	const saveOrderOrBooking = async () => {
		let result;

		if (readySaveOrBookingCheck()) {
			result = await fastSaveOrder();
		} else {
			result = await book();
		}

		return result;
	};

	return {
		bookOrEdit,
		book,
		saveOrderOrBook,
		addToWaitingList,
		removeFromWaitingList,
		saveOrderOrBooking,
		setToastAwaitId,
		toastAwaitId,
	};
};
